import React, { FC, ReactNode, useCallback } from 'react';
import classNames from 'classnames';
import { Button } from '@carbon/react';
import {
  SidePanelClose as SidePanelClose16,
  SidePanelOpen as SidePanelOpen16,
} from '@carbon/react/icons';
import styles from './dual-sidebar-layout.module.scss';

export interface SidebarConfig {
  content: ReactNode;
  isOpen: boolean;
  width?: number;
  headerControls?: ReactNode;
  sidebarClassName?: string;
}

interface DualSidebarLayoutProps {
  left?: SidebarConfig;
  right?: SidebarConfig;
  children: ReactNode;
  className?: string;
}

const Sidebar: FC<{
  side: 'left' | 'right';
  config: SidebarConfig;
}> = ({ side, config }) => {
  return (
    <aside
      className={classNames(
        styles.sidebar,
        styles[side],
        config.sidebarClassName,
        { [styles.collapsed]: !config.isOpen }
      )}
      style={
        config.width
          ? ({ '--sidebar-width': `${config.width}px` } as React.CSSProperties)
          : undefined
      }
    >
      <div className={styles.sidebarInner}>
        {config.headerControls && (
          <div className={styles.headerControls}>{config.headerControls}</div>
        )}
        <div className={styles.sidebarContent}>{config.content}</div>
      </div>
    </aside>
  );
};

export const SidebarToggle: FC<{
  side: 'left' | 'right';
  isOpen: boolean;
  onToggle: (open: boolean) => void;
  label?: string;
}> = ({ side, isOpen, onToggle, label }) => {
  const displayLabel = label ?? (side === 'left' ? 'Filters' : 'Panel');

  const handleToggle = useCallback(() => onToggle(!isOpen), [onToggle, isOpen]);
  const iconDescription = isOpen
    ? `Hide ${displayLabel}`
    : `Show ${displayLabel}`;

  return (
    <Button
      kind="tertiary"
      size="md"
      hasIconOnly
      className={styles.toggleBtn}
      onClick={handleToggle}
      iconDescription={iconDescription}
      aria-label={iconDescription}
      renderIcon={isOpen ? SidePanelClose16 : SidePanelOpen16}
      tooltipPosition="bottom"
    />
  );
};

export const DualSidebarLayout: FC<DualSidebarLayoutProps> = ({
  left,
  right,
  children,
  className,
}) => {
  return (
    <div className={classNames(styles.root, className)}>
      <div className={styles.body}>
        {left && <Sidebar side="left" config={left} />}
        <div className={styles.main}>{children}</div>
        {right && <Sidebar side="right" config={right} />}
      </div>
    </div>
  );
};
